import { gql } from '@apollo/client';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LOGIN_PATH } from 'src/common/constants';
import images from 'src/common/images';
import { ErrorMstOffice, MstOffice, MutationSet } from 'src/common/interfaces';
import { RecordState } from 'src/common/string-enum';
import { ErrorMessage, InfoMessage, InfoWord } from 'src/enum/messsage-enum';
import { deleteMstOffice, insertMstOffice, updateMstOffice } from 'src/graphql/mutations';
import { getMstOffice } from 'src/graphql/queries';
import { useAppLazyQuery, useAppMutation } from 'src/libs/hooks';
import utility from 'src/libs/utility';
import MainLayout from '../main-layout';

const defaultErrorMstOffice = {
  isErrorOfficeCd: false,
  isErrorOfficeNm: false,
};

const emptyOffice = {
  mo_address: '',
  mo_fax: '',
  mo_office_cd: '',
  mo_office_nm: '',
  mo_post: '',
  mo_tel: '',
  recordState: RecordState.INSERT,
  isCannotEditOfficeCd: false,
  error: defaultErrorMstOffice,
  isBeginData: false,
};

const OfficeManage = () => {
  const header = ['コード', '名称', '郵便番号', '住所', 'TEL', 'FAX', '削除'];
  const [mstOffice, setMstOffice] = useState<MstOffice[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuthenticator((context) => [context.user]);
  const { confirmAlert, showAlertMessage, registerData } = utility;
  const [removedMstOffice, setRemovedMstOffice] = useState<MstOffice[]>([]);
  const [beginLengthListOffice, setBeginLengthListOffice] = useState<number>(0);
  const [updateFunc] = useAppMutation({ query: gql(updateMstOffice) });
  const [deleteFunc] = useAppMutation({ query: gql(deleteMstOffice) });
  const [insertFunc] = useAppMutation({ query: gql(insertMstOffice) });
  const [searchMstOffice, { data: mstOfficeData, loading: isMstOfficeLoading }] = useAppLazyQuery({
    query: gql(getMstOffice),
    onCompleted: () => {
      setRemovedMstOffice([]);
    },
  });

  const renderTableHeader = useMemo(() => {
    return header.map((key, index) => (
      <th
        className="p-[8px] border-[1px] border-solid border-[#fff] pt-[12px] pb-[12px] text-center bg-[#a8d08d] text-[#fff]"
        key={index}
      >
        {key.toUpperCase()}
      </th>
    ));
  }, []);

  const renderTableData = useMemo(() => {
    if (!mstOffice || mstOffice.length <= 0) return;
    const styleInput = (isError: boolean | undefined) => {
      return `${
        isError ? 'border-[#FF0000] border-[2px] rounded-md' : 'border-{transparent}'
      } pl-[5px] bg-transparent w-full`;
    };
    return mstOffice.map((office: MstOffice, index: number) => {
      const isLastItem = index === mstOffice.length - 1;

      return (
        <tr className="hover:opacity-80 odd:bg-[#e2efd9]" key={index}>
          <td className="p-[8px] border-[1px] border-solid border-[#fff] ">
            <input
              className={`${styleInput(office.error.isErrorOfficeCd)} text-right`}
              type="number"
              onChange={(event) => {
                if (event.target.value.length === 9) return false;
                handleChangeOffice(event.target.value, index, 'mo_office_cd');
              }}
              name="id"
              value={office.mo_office_cd}
              disabled={office.isCannotEditOfficeCd}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={styleInput(office.error.isErrorOfficeNm)}
              onChange={(event) => handleChangeOffice(event.target.value, index, 'mo_office_nm')}
              type="text"
              name="name"
              value={office.mo_office_nm}
              maxLength={50}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={styleInput(false)}
              onChange={(event) => handleChangeOffice(event.target.value, index, 'mo_post')}
              type="text"
              name="postNumber"
              value={office.mo_post}
              maxLength={20}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={styleInput(false)}
              onChange={(event) => handleChangeOffice(event.target.value, index, 'mo_address')}
              type="text"
              name="email"
              value={office.mo_address}
              maxLength={100}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={styleInput(false)}
              onChange={(event) => handleChangeOffice(event.target.value, index, 'mo_tel')}
              type="text"
              name="tel"
              value={office.mo_tel}
              maxLength={20}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff]">
            <input
              className={styleInput(false)}
              onChange={(event) => handleChangeOffice(event.target.value, index, 'mo_fax')}
              type="text"
              name="fax"
              value={office.mo_fax}
              maxLength={20}
            />
          </td>
          <td className="p-[8px] border-[1px] border-solid border-[#fff] text-center">
            <button onClick={() => onPressConfirmRemove(index)}>
              {!isLastItem ? <img src={images.icDelete} alt="" className="w-[20px]" /> : null}
            </button>
          </td>
        </tr>
      );
    });
  }, [mstOffice]);

  const handleChangeOffice = (value: string, index: number, typeOffice: string) => {
    const newListMstOffice = mstOffice.slice();
    newListMstOffice[index] = { ...newListMstOffice[index], ...{ [typeOffice]: value } };
    const errorObject: ErrorMstOffice = { ...newListMstOffice[index].error };

    switch (typeOffice) {
      case 'mo_office_cd':
        errorObject.isErrorOfficeCd = false;
        break;
      case 'mo_office_nm':
        errorObject.isErrorOfficeNm = false;
        break;
    }
    newListMstOffice[index].error = { ...newListMstOffice[index].error, ...errorObject };
    if (newListMstOffice[index].isBeginData) {
      newListMstOffice[index].recordState = RecordState.UPDATE;
    }
    setMstOffice(newListMstOffice);
  };

  const isEmptyData = (data: MstOffice) => {
    return (
      data.mo_address === '' &&
      data.mo_fax === '' &&
      data.mo_office_nm === '' &&
      data.mo_post === '' &&
      data.mo_tel === '' &&
      data.mo_office_cd === ''
    );
  };

  const onPressRemove = (index: number) => {
    const newListOffice = mstOffice.slice();
    if (index < beginLengthListOffice) {
      const newListRemove = [...removedMstOffice];
      const dataItem = mstOffice[index];
      setBeginLengthListOffice(beginLengthListOffice - 1);
      newListRemove.push({ ...dataItem, recordState: RecordState.DELETE });
      setRemovedMstOffice(newListRemove);
    }
    newListOffice.splice(index, 1);
    setMstOffice(newListOffice);
  };

  const prepareDataApplyApi = (officeData: MstOffice) => {
    return {
      mo_address: officeData.mo_address,
      mo_fax: officeData.mo_fax,
      mo_office_cd: officeData.mo_office_cd,
      mo_office_nm: officeData.mo_office_nm,
      mo_post: officeData.mo_post,
      mo_tel: officeData.mo_tel,
    };
  };

  const onPressConfirmRemove = (index: number) => {
    confirmAlert(InfoMessage.CONFIRM_REMOVE, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, () =>
      onPressRemove(index)
    );
  };

  const checkError = (itemHinmei: MstOffice) => {
    const isErrorOfficeCd = itemHinmei.mo_office_cd === '';
    const isErrorOfficeNm = itemHinmei.mo_office_nm === '';
    return { isErrorOfficeCd, isErrorOfficeNm };
  };

  const isExistItem = () => {
    const newListOffice = mstOffice.slice();
    const valueArr = newListOffice.map((item) => {
      return item.mo_office_cd.toString();
    });
    const isDuplicate = valueArr.some((item, idx) => {
      if (valueArr.indexOf(item) !== idx) {
        newListOffice[idx].error.isErrorOfficeCd = true;
        return true;
      }
      return false;
    });
    setMstOffice(newListOffice);
    return isDuplicate;
  };

  const validates = () => {
    const newListOffice = mstOffice.slice();
    newListOffice.pop();
    const newListAddError = newListOffice.map((itemOffice: MstOffice) => {
      return { ...itemOffice, error: checkError(itemOffice) };
    });
    setMstOffice(newListAddError);
    const indexItemError = newListOffice.findIndex(
      (itemOffice: MstOffice) => itemOffice.mo_office_cd === '' || itemOffice.mo_office_nm === ''
    );
    if (indexItemError !== -1) {
      showAlertMessage('', ErrorMessage.INPUT_ERROR, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    if (isExistItem()) {
      showAlertMessage('', InfoMessage.REGISTR_DUPLICATION, InfoWord.ERROR, InfoWord.OK);
      return false;
    }
    return true;
  };

  const executeRegister = async () => {
    const mstOfficeNewList = mstOffice.slice();
    const insertData = mstOfficeNewList.filter((item: MstOffice) => item.recordState === RecordState.INSERT);
    const updateData = mstOfficeNewList.filter((item: MstOffice) => item.recordState === RecordState.UPDATE);

    // 最終行(新規行)を除外
    insertData.pop();

    const delSet: MutationSet<MstOffice> = {
      recordState: RecordState.DELETE,
      data: removedMstOffice,
      func: (data: MstOffice) => deleteFunc({ variables: { input: { mo_office_cd: data.mo_office_cd } } }),
    };
    const updSet: MutationSet<MstOffice> = {
      recordState: RecordState.UPDATE,
      data: updateData,
      func: (data: MstOffice) => updateFunc({ variables: { input: prepareDataApplyApi(data) } }),
    };
    const insSet: MutationSet<MstOffice> = {
      recordState: RecordState.INSERT,
      data: insertData,
      func: (data: MstOffice) => insertFunc({ variables: { input: prepareDataApplyApi(data) } }),
    };

    const searchFunc = () => searchMstOffice({ variables: { input: {} } });

    registerData<MstOffice>({
      mutationSets1: [delSet, updSet, insSet],
      validates,
      searchFunc,
    });
  };

  const onRegisterData = async () => {
    Auth.currentAuthenticatedUser()
      .then(() =>
        confirmAlert(InfoMessage.CONFIRM_REGISTR, '', InfoWord.WARNING, InfoWord.YES, InfoWord.NO, executeRegister)
      )
      .catch(() => {
        signOut();
        navigate(LOGIN_PATH, { replace: true, state: { from: location } });
      });
  };

  useEffect(() => {
    if (mstOffice?.length < 1) return;
    const lastIndex = mstOffice.length - 1;
    const newListMstOffice = mstOffice.slice();

    if (!isEmptyData(newListMstOffice[lastIndex])) {
      newListMstOffice.push(emptyOffice);
      setMstOffice(newListMstOffice);
    }
  }, [mstOffice]);

  useEffect(() => {
    if (!mstOfficeData) return;

    const newListOffice = mstOfficeData.getMstOffice.map((item: MstOffice) => {
      return {
        ...item,
        recordState: RecordState.NONE,
        isCannotEditOfficeCd: true,
        error: defaultErrorMstOffice,
        isBeginData: true,
      };
    });
    setBeginLengthListOffice(mstOfficeData.getMstOffice.length);
    newListOffice.sort((a: MstOffice, b: MstOffice) => Number(a.mo_office_cd) - Number(b.mo_office_cd));
    newListOffice.push(emptyOffice);
    setMstOffice(newListOffice);
  }, [mstOfficeData]);

  useEffect(() => {
    searchMstOffice({ variables: { input: {} } });
  }, []);

  return (
    <MainLayout>
      <div className="flex flex-col justify-center items-center">
        <p className="p-[20px]">拠点管理</p>
        <div className="flex w-4/5">
          <button
            onClick={onRegisterData}
            className="border-[#000000] border-[1px] p-[10px] mt-[25px] mb-[25px] text-center h-[50px] w-[100px] font-bold"
          >
            登録
          </button>
        </div>
        <table className="border-collapse border-[3px] border-solid border-[#ddd] w-4/5 td:p-[10px]">
          <thead>
            <tr className="hover:opacity-80 even:bg-[#e2efd9]">{renderTableHeader}</tr>
          </thead>
          <tbody>
            {isMstOfficeLoading ? (
              <tr className="hover:opacity-80 even:bg-[#e2efd9]">
                <td
                  colSpan={header.length}
                  className="text-center animate-pulse p-[8px] border-[1px] border-solid border-[#fff]"
                >
                  Loading...
                </td>
              </tr>
            ) : (
              renderTableData
            )}
          </tbody>
        </table>
      </div>
    </MainLayout>
  );
};

export default OfficeManage;
